import React, { useMemo, useState } from 'react';
import CollapsibleSection from '../CollapsibleSection';
import WorkoutCompositionPicker from '../WorkoutCompositionPicker';
import { INTEREST_PRESETS, appendInterestChip } from '../../constants/profileOptions';
import { LIMITATION_OPTIONS } from '../../constants/trainingProfileOptions';
import { parseTrainingProfile } from '../../constants/trainingProfileOptions';

function LimitationChips({ limitations, onToggle }) {
  const set = useMemo(() => new Set(limitations), [limitations]);
  return (
    <div className="flex flex-wrap gap-2">
      {LIMITATION_OPTIONS.map((opt) => {
        const on = set.has(opt.slug);
        return (
          <button
            key={opt.slug}
            type="button"
            onClick={() => onToggle(opt.slug)}
            className={`rounded-lg border px-3 py-2 font-mono text-xs transition-colors ${
              on
                ? 'border-iron-accent bg-iron-accent text-black font-medium'
                : 'border-iron-border text-iron-muted hover:border-iron-accent hover:text-iron-accent'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default function WizardStepLimitations({
  formData,
  onChange,
  globalNotes,
  onGlobalNotesChange,
  extraDetail,
  onExtraDetailChange,
  workoutComposition,
  onWorkoutCompositionChange,
}) {
  const profile = useMemo(() => parseTrainingProfile(formData.trainingProfile), [formData.trainingProfile]);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const patch = (partial) => onChange({ ...formData, ...partial });

  const toggleLimitation = (slug) => {
    const list = profile.limitations || [];
    const has = list.includes(slug);
    let next;
    if (slug === 'none') {
      next = has ? [] : ['none'];
    } else {
      next = has ? list.filter((s) => s !== slug) : [...list.filter((s) => s !== 'none'), slug];
    }
    patch({
      trainingProfile: { ...profile, limitations: next },
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-wider text-iron-accent">Limitações e preferências</p>
        <p className="mt-1 text-xs text-iron-muted">
          Estado físico e interesses são obrigatórios para gerar o pack.
        </p>
      </div>

      <div>
        <label className="section-label mb-2 block">Limitações físicas</label>
        <LimitationChips limitations={profile.limitations || []} onToggle={toggleLimitation} />
      </div>

      <div>
        <label className="section-label mb-2 block">Estado físico atual</label>
        <textarea
          value={formData.physicalState}
          onChange={(e) => patch({ physicalState: e.target.value })}
          className="input min-h-[88px] resize-y"
          placeholder="Ex.: Treino 4x por semana há 2 anos. Ombro direito incomoda no supino."
        />
        <p className="mt-1 font-mono text-[10px] text-iron-muted">Mínimo 10 caracteres</p>
      </div>

      <div>
        <label className="section-label mb-2 block">Interesses e preferências</label>
        <textarea
          value={formData.trainingInterests}
          onChange={(e) => patch({ trainingInterests: e.target.value })}
          className="input min-h-[88px] resize-y"
          placeholder="PPL preferido, halteres, evitar leg press…"
        />
        <p className="mt-1 font-mono text-[10px] text-iron-muted">Mínimo 20 caracteres</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {INTEREST_PRESETS.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() =>
                patch({
                  trainingInterests: appendInterestChip(formData.trainingInterests, chip),
                })
              }
              className="rounded-full border border-iron-border px-2.5 py-1 font-mono text-[10px] text-iron-muted hover:border-iron-accent hover:text-iron-accent"
            >
              {chip}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="section-label mb-2 block">Composição da sessão</label>
        <p className="mb-2 text-xs text-iron-muted">
          Você pode marcar vários segmentos; o treino principal fica sempre incluído.
        </p>
        <WorkoutCompositionPicker
          value={workoutComposition}
          onChange={(next, meta) => onWorkoutCompositionChange?.(next, meta)}
          title="Segmentos do treino"
        />
      </div>

      <CollapsibleSection
        title="Notas e contexto (opcional)"
        open={advancedOpen}
        onOpenChange={setAdvancedOpen}
      >
        <div className="space-y-3">
          <label className="block">
            <span className="font-mono text-[10px] text-iron-muted">Contexto geral</span>
            <textarea
              value={globalNotes}
              onChange={(e) => onGlobalNotesChange?.(e.target.value)}
              rows={2}
              className="input mt-1 min-h-[60px] resize-y font-mono text-xs"
              placeholder="Ex.: foco em membros superiores…"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] text-iron-muted">Histórico de treino (opcional)</span>
            <textarea
              value={formData.previousTrainingBase}
              onChange={(e) => patch({ previousTrainingBase: e.target.value })}
              rows={2}
              className="input mt-1 min-h-[60px] resize-y font-mono text-xs"
              placeholder="Ex.: PPL 6x, foco hipertrofia, 8 semanas…"
              maxLength={16000}
            />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] text-iron-muted">Pedido desta geração (opcional)</span>
            <textarea
              value={extraDetail}
              onChange={(e) => onExtraDetailChange?.(e.target.value)}
              rows={2}
              className="input mt-1 min-h-[60px] resize-y font-mono text-xs"
              placeholder="Equipamento, dias disponíveis…"
            />
          </label>
        </div>
      </CollapsibleSection>
    </div>
  );
}
